import React, { useState, useEffect, useCallback } from 'react';
import styles from './BulkMenuActions.module.css';

export interface MenuImportState {
  previewCount: number;
  importItems: () => void;
}

interface BulkMenuActionsProps {
  onBulkImport?: (items: Record<string, string>[]) => void;
  isLoading?: boolean;
  importOpen?: boolean;
  onImportOpenChange?: (open: boolean) => void;
  onImportStateChange?: (state: MenuImportState) => void;
}

const BulkMenuActions: React.FC<BulkMenuActionsProps> = ({
  onBulkImport,
  isLoading = false,
  importOpen = false,
  onImportOpenChange,
  onImportStateChange,
}) => {
  const [importPreview, setImportPreview] = useState<Record<string, string>[]>([]);
  const [importError, setImportError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');

  useEffect(() => {
    if (!importOpen) {
      setImportPreview([]);
      setImportError('');
      setFileName('');
    }
  }, [importOpen]);

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const items: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim());
      const item: Record<string, string> = {};

      headers.forEach((header, index) => {
        item[header] = values[index] || '';
      });

      if (item.name && item.price) {
        items.push(item);
      }
    }

    if (items.length === 0) {
      throw new Error('No valid menu items found (items must have at least "name" and "price")');
    }

    return items;
  };

  const parseXML = (text: string): Record<string, string>[] => {
    const itemRegex = /<item>([\s\S]*?)<\/item>/g;
    const fieldRegex = /<(\w+)>(.*?)<\/\1>/g;
    const items: Record<string, string>[] = [];

    let itemMatch;
    while ((itemMatch = itemRegex.exec(text)) !== null) {
      const itemContent = itemMatch[1];
      const item: Record<string, string> = {};

      let fieldMatch;
      while ((fieldMatch = fieldRegex.exec(itemContent)) !== null) {
        const [, fieldName, fieldValue] = fieldMatch;
        item[fieldName.toLowerCase()] = fieldValue;
      }

      if (Object.keys(item).length > 0) {
        items.push(item);
      }
    }

    if (items.length === 0) {
      throw new Error('No valid menu items found in XML file');
    }

    return items;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setImportError('');

    try {
      const text = await file.text();
      let parsedData: Record<string, string>[] = [];

      if (file.name.toLowerCase().endsWith('.csv')) {
        parsedData = parseCSV(text);
      } else if (file.name.toLowerCase().endsWith('.xml')) {
        parsedData = parseXML(text);
      } else {
        throw new Error('Unsupported file format. Please use CSV or XML files.');
      }

      setImportPreview(parsedData);
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Failed to process file');
      setImportPreview([]);
    }
  };

  const handleImport = useCallback(() => {
    if (importPreview.length > 0 && onBulkImport) {
      onBulkImport(importPreview);
      onImportOpenChange?.(false);
    }
  }, [importPreview, onBulkImport, onImportOpenChange]);

  useEffect(() => {
    onImportStateChange?.({
      previewCount: importPreview.length,
      importItems: handleImport,
    });
  }, [importPreview.length, handleImport, onImportStateChange]);

  if (!importOpen || !onBulkImport) {
    return null;
  }

  return (
    <div className={styles.importBody}>
      <div className={styles.fileUploadArea}>
        <input
          type="file"
          accept=".csv,.xml"
          onChange={handleFileChange}
          id="bulk-import-file"
          className={styles.fileInput}
          disabled={isLoading}
        />
        <label htmlFor="bulk-import-file" className={styles.fileLabel}>
          <div className={styles.fileLabelText}>
            {fileName ? `Selected: ${fileName}` : 'Click to select a CSV or XML file'}
          </div>
        </label>
      </div>

      <div className={styles.formatHelp}>
        <h4>File format</h4>
        <p>
          <strong>CSV:</strong> columns name, price (optional: description, category)
        </p>
        <p>
          <strong>XML:</strong> &lt;item&gt;&lt;name&gt;…&lt;/name&gt;&lt;price&gt;…&lt;/price&gt;&lt;/item&gt;
        </p>
      </div>

      {importError && <div className={styles.errorMessage}>{importError}</div>}

      {importPreview.length > 0 && (
        <div className={styles.previewSection}>
          <h4>Preview ({importPreview.length} items)</h4>
          <div className={styles.previewList}>
            {importPreview.slice(0, 5).map((item, index) => (
              <div key={index} className={styles.previewItem}>
                <div className={styles.previewItemName}>{item.name}</div>
                <div className={styles.previewItemPrice}>${parseFloat(item.price).toFixed(2)}</div>
                <div className={styles.previewItemDesc}>{item.description || '—'}</div>
              </div>
            ))}
          </div>
          {importPreview.length > 5 && (
            <div className={styles.previewMore}>… and {importPreview.length - 5} more</div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkMenuActions;
